import * as React from 'react';
import MuiButton from '@mui/material/Button';

type RSColor = 'primary' | 'secondary' | 'success' | 'danger' | 'info' | 'warning' | 'link' | 'light' | 'outline';

export interface AppButtonProps extends Record<string, any> {
    color?: RSColor;
    outline?: boolean;
    active?: boolean;
    disabled?: boolean;
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
    type?: 'button' | 'submit' | 'reset';
    className?: string;
    id?: string;
    style?: React.CSSProperties;
    title?: string;
    size?: 'sm' | 'lg';
    block?: boolean;
    href?: string;
    target?: string;
    rel?: string;
    children?: React.ReactNode;
}

function resolveMuiProps(color?: RSColor, outline?: boolean) {
    if (color === 'link') {
        return { variant: 'text', muiColor: 'inherit' } as const;
    }
    if (outline || color === 'light' || color === 'outline') {
        const muiColor =
            color === 'success' ? 'success' :
            color === 'danger' ? 'error' :
            color === 'info' ? 'info' :
            color === 'warning' ? 'warning' :
            color === 'primary' ? 'primary' :
            'secondary';
        return { variant: 'outlined', muiColor } as const;
    }
    switch (color) {
        case 'primary': return { variant: 'contained', muiColor: 'primary' } as const;
        case 'success': return { variant: 'contained', muiColor: 'success' } as const;
        case 'danger':  return { variant: 'contained', muiColor: 'error' } as const;
        case 'info':    return { variant: 'contained', muiColor: 'info' } as const;
        case 'warning': return { variant: 'contained', muiColor: 'warning' } as const;
        case 'secondary':
        default:        return { variant: 'contained', muiColor: 'secondary' } as const;
    }
}

const AppButton: React.FC<AppButtonProps> = ({
    color,
    outline,
    active,
    disabled,
    onClick,
    type = 'button',
    className,
    id,
    style,
    title,
    size,
    block,
    href,
    target,
    rel,
    children,
    ...rest
}) => {
    const { variant, muiColor } = resolveMuiProps(color, outline);
    const muiSize = size === 'sm' ? 'small' : size === 'lg' ? 'large' : 'medium';
    const classes = ['app-button', className, active ? 'app-button--active' : ''].filter(Boolean).join(' ');

    return (
        <MuiButton
            {...rest}
            variant={variant}
            color={muiColor as any}
            disabled={disabled}
            onClick={onClick}
            type={type}
            className={classes}
            id={id}
            style={muiSize === 'large' ? { minHeight: '4rem', ...style } : style}
            title={title}
            size={muiSize}
            fullWidth={block}
            href={href}
            {...(target ? { target } : {})}
            {...(rel ? { rel } : {})}
            aria-pressed={active}
            sx={{ gap: 'var(--spacer-XS)' }}
        >
            {children}
        </MuiButton>
    );
};

export default AppButton;
